"use client";

import clsx from "clsx";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Briefcase01Icon,
  Calendar01Icon,
  Home01Icon,
  Notification01Icon,
  Profile02Icon,
  StarIcon,
} from "hugeicons-react";
import Link from "next/link";
import { routes } from "@/lib/utils/routes";
import Image from "next/image";
import Logo from "@/assets/imgs/logo_white_no_bg.png";
import { usePathname } from "next/navigation";
import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useClientTranslations } from "@/lib/translate";
import { useSidebarStore } from "@/modules/store/sidebarStore";
import { useNotificationStore } from "@/modules/store/notificationStore";
import dynamic from "next/dynamic";
import { LoadingHeader } from "@/modules/components/commons/Header/HeaderManager";
import HeaderManager from "@/modules/components/commons/Header/HeaderManager";

const Notifications = dynamic(
  () => import("@/modules/components/notifications/Notifications"),
  {
    ssr: false,
  },
);

interface SubMenuItem {
  key: string;
  label: string;
  selected: boolean;
  href: string;
}

interface MenuItem {
  key: string;
  label: string;
  selected: boolean;
  href?: string;
  icon: React.ReactNode;
  isOpen?: boolean;
  submenu?: SubMenuItem[];
}

const Sidebar = memo(({ children }: { children?: React.ReactNode }) => {
  const { t } = useClientTranslations("sidebar-manager");
  const { isOpenSidebarLeft, toggleSidebarLeft } = useSidebarStore();
  const { notReadNoti } = useNotificationStore();
  const pathname = usePathname();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const handleClickPanel = () => buttonRef.current?.click();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([
    {
      key: "dashboard",
      label: t("dashboard"),
      selected: false,
      href: routes.home,
      icon: <Home01Icon />,
    },
    {
      key: "jobs",
      label: t("jobs.list"),
      selected: false,
      icon: <Briefcase01Icon />,
      submenu: [
        {
          key: "jobList",
          label: t("jobs.list"),
          selected: false,
          href: routes.job.list,
        },
        {
          key: "jobCreate",
          label: t("jobs.create"),
          selected: false,
          href: routes.job.create,
        },
      ],
    },
    {
      key: "applicants",
      label: t("applicants"),
      selected: false,
      href: routes.applicants.list,
      icon: <Profile02Icon />,
    },
    {
      key: "interviewCalendar",
      label: t("interviewCalendar"),
      selected: false,
      href: routes.interview.schedules,
      icon: <Calendar01Icon />,
    },
    {
      key: "reviews",
      label: t("reviews"),
      selected: false,
      href: routes.company.reviews,
      icon: <StarIcon />,
    },
    {
      key: "notifications",
      label: t("notifications"),
      selected: false,
      href: routes.notifications,
      icon: <Notification01Icon />,
    },
  ]);

  const checkMenuItemSelected = useCallback(() => {
    const newMenuItems = menuItems.map((item) => {
      if (item.submenu) {
        const hasSelectedSubmenu = item.submenu.some(
          (sub) => sub.href === pathname,
        );
        return {
          ...item,
          selected: hasSelectedSubmenu,
          isOpen: hasSelectedSubmenu,
          submenu: item.submenu.map((sub) => ({
            ...sub,
            selected: sub.href === pathname,
          })),
        };
      }
      return {
        ...item,
        selected: item.href === pathname,
      };
    });

    setMenuItems(newMenuItems);
  }, [pathname]);

  useEffect(() => {
    checkMenuItemSelected();
  }, [pathname, checkMenuItemSelected]);

  const handleToggleSubmenu = (key: string) => {
    setMenuItems((prev) =>
      prev.map((item) => ({
        ...item,
        isOpen: item.key === key ? !item.isOpen : false,
      })),
    );
  };

  return (
    <>
      <div
        className={clsx(
          "z-[5] bg-[var(--background)] flex top-0 left-0 h-full fixed flex-col transition-all duration-300 ease-in-out border-r-[1px] border-gray-200 dark:border-gray-800",
          `${"w-[var(--layout-nav-vertical-width)]"}`,
          // `${
          //   isOpenSidebarLeft
          //     ? "translate-x-0"
          //     : `-translate-x-[calc(var(--layout-nav-vertical-width)-var(--layout-nav-mini-width))]`
          // }`
          `${isOpenSidebarLeft
            ? "translate-x-0 lg:translate-x-0 lg:w-[var(--layout-nav-vertical-width)]"
            : "-translate-x-full lg:translate-x-0 lg:w-[var(--layout-nav-mini-width)]"
          }`,
        )}
      >
        <div className="fixed w-full flex items-center justify-start px-3 h-[var(--layout-header-mobile-height-sm)] md:h-[var(--layout-header-mobile-height)] lg:h-[var(--layout-header-desktop-height)]">
          <Link href={routes.home} className="p-1 rounded-md bg-white">
            <Image
              className="w-16"
              src={Logo}
              alt="Togoether Work logo"
              width={100}
              height={100}
            />
          </Link>
          <button
            className="z-[5] flex absolute top-3 lg:top-10 right-0 -translate-x-2 lg:translate-x-1/2 w-7 h-7 p-1 items-center justify-center rounded-full bg-white dark:bg-black hover:bg-gray-100 dark:hover:bg-gray-900 border border-gray-200 dark:border-gray-800"
            onClick={toggleSidebarLeft}
          >
            {isOpenSidebarLeft ? <ArrowLeft01Icon /> : <ArrowRight01Icon />}
          </button>
        </div>
        <nav className="mt-[var(--layout-header-mobile-height-sm)] md:mt-[var(--layout-header-mobile-height)] lg:mt-[var(--layout-header-desktop-height)] w-full overflow-y-auto p-4 transition-all duration-300 ease-in-out">
          <ul
            key="menu"
            className="list-none flex flex-col gap-1 flex-1 text-sm"
          >
            {menuItems.map((value, key) =>
              value.submenu ? (
                isOpenSidebarLeft ? (
                  <li
                    key={key}
                    className="transition-all duration-300 ease-in-out relative"
                  >
                    <button
                      onClick={() => handleToggleSubmenu(value.key)}
                      data-popover-target={`popover-${value.key}`}
                      className={`w-full transition-all duration-300 ease-in-out hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-1 flex items-center ${isOpenSidebarLeft
                        ? "justify-between gap-2"
                        : "justify-center"
                        }`}
                    >
                      <div className="flex flex-row gap-2 items-center">
                        {value.icon}
                        {isOpenSidebarLeft && <span>{value.label}</span>}
                      </div>
                      {value.submenu && value.submenu.length > 0 && (
                        <ArrowRight01Icon
                          className={`transition-all duration-300 ${value.isOpen ? "rotate-90" : ""}`}
                        />
                      )}
                    </button>
                    <div
                      className={`rounded-lg flex flex-col gap-1 flex-1 bg-gray-100 dark:bg-gray-800 overflow-hidden transition-all duration-300 ${isOpenSidebarLeft
                        ? value.isOpen
                          ? "h-fit p-2 mt-2"
                          : "h-0"
                        : "hidden"
                        }`}
                    >
                      <ul className="list-none flex gap-2 flex-col">
                        {value.submenu.map((sub, subKey) => (
                          <Link key={subKey} href={sub.href}>
                            <li
                              className={clsx(
                                `hover:bg-white dark:hover:bg-gray-700 rounded-lg p-1 transition-colors duration-300`,
                                sub.selected
                                  ? "bg-white dark:bg-gray-700 text-primary"
                                  : "",
                              )}
                            >
                              {sub.label}
                            </li>
                          </Link>
                        ))}
                      </ul>
                    </div>
                  </li>
                ) : (
                  <li
                    key={key}
                    className="transition-all duration-300 ease-in-out relative"
                  >
                    <Popover>
                      <PopoverButton
                        ref={buttonRef}
                        className="flex items-center w-full h-full transition-colors duration-300 ease-in-out hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-2 justify-center focus:outline-none"
                      >
                        {value.icon}
                      </PopoverButton>
                      <PopoverPanel
                        onClick={handleClickPanel}
                        onKeyDown={handleClickPanel}
                        transition
                        anchor="right"
                        className="z-[55] divide-y divide-gray-200 dark:divide-gray-800 rounded-xl bg-white dark:bg-black text-sm/6 transition duration-200 ease-in-out [--anchor-gap:var(--spacing-5)] data-[closed]:-translate-y-1 data-[closed]:opacity-0 p-1 min-w-36 border border-gray-200 dark:border-gray-800"
                      >
                        {value.submenu?.map((sub, subKey) => (
                          <Link
                            onClick={handleClickPanel}
                            key={subKey}
                            href={sub.href}
                            className="w-full"
                          >
                            <span className="block w-full rounded-lg p-1 transition hover:bg-gray-100 dark:hover:bg-gray-700">
                              {sub.label}
                            </span>
                          </Link>
                        ))}
                      </PopoverPanel>
                    </Popover>
                  </li>
                )
              ) : (
                <Link
                  key={key}
                  href={value.href ?? ""}
                  className="transition-all duration-300 ease-in-out"
                >
                  <li
                    className={`flex items-center transition-colors duration-300 ease-in-out hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-1 ${isOpenSidebarLeft
                      ? "justify-start gap-2"
                      : "justify-center"
                      } ${value.selected ? "bg-gray-100 dark:bg-gray-700 text-primary" : ""}`}
                  >
                    {value.icon}
                    {isOpenSidebarLeft && <span>{value.label}</span>}
                    {value.key === "notifications" && notReadNoti.total > 0 && (
                      <span className={clsx(
                        "flex items-center justify-center bg-primary text-white rounded-full text-[10px] font-bold p-1",
                        isOpenSidebarLeft ? "ml-auto size-5" : "absolute top-0 right-0 size-4 translate-x-1/3 -translate-y-1/3 border-2 border-white dark:border-gray-800"
                      )}>
                        {notReadNoti.total > 99 ? "99+" : notReadNoti.total}
                      </span>
                    )}
                  </li>
                </Link>
              ),
            )}
          </ul>
        </nav>
      </div>
      <div
        className={`w-full transition-all duration-300 ease-in-out ${isOpenSidebarLeft
          ? "lg:w-[calc(100%-var(--layout-nav-vertical-width))] lg:ml-[var(--layout-nav-vertical-width)]"
          : "lg:w-[calc(100%-var(--layout-nav-mini-width))] lg:ml-[var(--layout-nav-mini-width)]"
          }`}
      >
        <Suspense fallback={<LoadingHeader />}>
          <HeaderManager />
        </Suspense>
        <div className="mx-auto">{children}</div>
      </div>
      <Notifications />
    </>
  );
});

Sidebar.displayName = "Sidebar";

export default Sidebar;
